import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Play, Check, ArrowLeft, Clock } from "lucide-react";
import api from "@/lib/api";
import MemberLayout from "@/components/layout/MemberLayout";

export default function MemberCourse() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/member/courses/${id}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.course.title} — Ana Paula Moraes`;
    });
  }, [id]);

  if (!data) {
    return <MemberLayout><div className="p-8">Carregando…</div></MemberLayout>;
  }

  const { course, modules, completed_lesson_ids, progress_pct, completed_count, total_lessons } = data;
  const completedSet = new Set(completed_lesson_ids);

  return (
    <MemberLayout>
      <div className="p-8 lg:p-16 max-w-6xl mx-auto">
        <Link to="/membros" className="inline-flex items-center gap-2 text-sm mb-6 hover:text-[color:var(--copper)]" style={{ color: "var(--text-secondary)" }} data-testid="course-back">
          <ArrowLeft size={14} /> Voltar
        </Link>

        <div className="grid lg:grid-cols-3 gap-12 mb-12">
          <div className="lg:col-span-2">
            <div className="eyebrow mb-3">Curso</div>
            <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-4" style={{ color: "var(--text-primary)" }}>{course.title}</h1>
            <p className="text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>{course.description}</p>

            <div className="mt-8">
              <div className="flex justify-between text-sm mb-2" style={{ color: "var(--text-secondary)" }}>
                <span>Progresso: {completed_count}/{total_lessons} aulas</span>
                <span>{progress_pct}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--border)" }}>
                <div className="h-full transition-all" style={{ width: `${progress_pct}%`, background: "linear-gradient(90deg, var(--rose-gold), var(--copper))" }} />
              </div>
            </div>
          </div>
          {course.cover_url && (
            <div className="rounded-2xl overflow-hidden">
              <img src={course.cover_url} alt={course.title} className="w-full h-full object-cover" />
            </div>
          )}
        </div>

        <div className="space-y-6">
          {modules.map((m, mi) => (
            <div key={m.id} className="brand-card p-6 md:p-8" data-testid={`module-${mi}`}>
              <div className="flex items-baseline gap-3 mb-4">
                <span className="font-serif italic text-lg" style={{ color: "var(--copper)" }}>Módulo {mi + 1}</span>
                <h2 className="font-serif text-2xl" style={{ color: "var(--text-primary)" }}>{m.title}</h2>
              </div>
              {m.description && <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>{m.description}</p>}
              <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                {m.lessons.map((l, li) => {
                  const done = completedSet.has(l.id);
                  return (
                    <li key={l.id}>
                      <Link to={`/membros/aula/${l.id}`} className="flex items-center gap-4 py-4 hover:bg-[color:var(--bg)] -mx-2 px-2 rounded-lg transition-colors" data-testid={`lesson-${mi}-${li}`}>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: done ? "var(--rose-gold)" : "rgba(196,143,149,0.15)" }}>
                          {done ? <Check size={16} className="text-white" /> : <Play size={14} style={{ color: "var(--copper)" }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate" style={{ color: "var(--text-primary)" }}>{l.title}</div>
                          {l.duration && (
                            <div className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "var(--text-secondary)" }}>
                              <Clock size={11} /> {l.duration}
                            </div>
                          )}
                        </div>
                      </Link>
                    </li>
                  );
                })}
                {m.lessons.length === 0 && (
                  <li className="py-3 text-sm" style={{ color: "var(--text-secondary)" }}>Nenhuma aula publicada ainda.</li>
                )}
              </ul>
            </div>
          ))}
          {modules.length === 0 && (
            <div className="brand-card p-10 text-center" style={{ color: "var(--text-secondary)" }}>
              Os módulos deste curso ainda estão sendo preparados.
            </div>
          )}
        </div>
      </div>
    </MemberLayout>
  );
}
